import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import {
  formatABN,
  formatMobile,
  formatName,
  formatPhone,
} from "../../lib/Misc";
import { debounce } from "lodash";
import MainBody from "../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../components/Pagination";
import sortCaret from "../../components/SortCaret";
// import SyncXero from "../../components/SyncXero";
import UploadBeneficary from "./UploadBeneficary";
import AddEditBeneficary from "./AddEditBeneficary";
import { ResponsiveDrawerForm } from "../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import BeneficaryActionButton from "./BeneficaryActionButton";
import Confirmation from "../../components/Confirmation";
import { useTrust } from "../../lib/context/TrustContext";

const Beneficiarys = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [beneficarysList, setBeneficiarysList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const { trustId } = useTrust();
  // const builderId = localStorage.getItem("builderId")
  //   ? localStorage.getItem("builderId")
  //   : "";

  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const [beneficaryId, setBeneficaryId] = useState(null);
  const [showAddEditBeneficary, setShowAddEditBeneficary] = useState(false);
  const [showBeneficaryDelete, setShowBeneficaryDelete] = useState(false);

  const toggleShowAddEditBeneficary = () => {
    if (showAddEditBeneficary) {
      setEdit(false);
      setBeneficaryId(null);
    }
    setShowAddEditBeneficary(!showAddEditBeneficary);
  };

  const toggleShowDelete = () => {
    if (showBeneficaryDelete) {
      setBeneficaryId(null);
    }
    setShowBeneficaryDelete(!showBeneficaryDelete);
  };

  const fetchBeneficary = useCallback(
    async (passLimit, passPage) => {
      if (trustId) {
        try {
          setLoading(true);
          const { data } = await get(
            `${API.GET_BENEFICARYS}?limit=${
              passLimit ? passLimit : limit ? limit : defaultLimitPagination
            }&page=${passPage ? passPage : page ? page : 1}&search=${
              searchParam ? searchParam : ""
            }&sort_column=${sortField ? sortField : ""}&sort_order=${
              sortOrder ? sortOrder : ""
            }&trust_id=${trustId ? trustId : ""}`
          );

          setLoading(false);
          setLimit(data?.data?.per_page);
          setPage(data?.data?.current_page);
          setTotal(data?.data?.total);
          setFrom(data?.data?.from);
          setTo(data?.data?.to);
          const beneficarysList = data.data.data;
          let notSelectedIds = [];
          for (let i = 0; i < beneficarysList?.length; i++) {
            notSelectedIds.push(beneficarysList[i]?.id);
          }
          // setNotSelectedRowIds(notSelectedIds);
          setBeneficiarysList(beneficarysList);
          return data.data;
        } catch (error) {
          setLoading(false);
          setBeneficiarysList([]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField, trustId]
  );

  const beneficarysGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: beneficarysList[i]?.id,
        code: beneficarysList[i]?.code ? beneficarysList[i]?.code : "-",
        name: formatName(
          beneficarysList[i]?.first_name,
          beneficarysList[i]?.last_name
        ),
        email: beneficarysList[i]?.email,
        abn: beneficarysList[i]?.abn ? formatABN(beneficarysList[i]?.abn) : "-",
        phone: beneficarysList[i]?.phone
          ? formatPhone(beneficarysList[i]?.phone)
          : "-",
        mobile: beneficarysList[i]?.mobile
          ? formatMobile(beneficarysList[i]?.mobile)
          : "-",
        business_name: beneficarysList[i]?.business_name
          ? beneficarysList[i]?.business_name
          : "-",
        source: beneficarysList[i]?.import_from
          ? beneficarysList[i]?.import_from
          : "TrustSoft",
        action: (
          <>
            <BeneficaryActionButton
              id={beneficarysList[i]?.id}
              setEdit={setEdit}
              used={beneficarysList[i]?.is_used ? true : false}
              setBeneficaryId={setBeneficaryId}
              toggleShowAddEditBeneficary={toggleShowAddEditBeneficary}
              toggleShowDelete={toggleShowDelete}
            />
          </>
        ),
      });
    }
    return items;
  };

  const beneficarys = beneficarysGenerator(beneficarysList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "code",
      text: "Code",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "name",
      text: "Name",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "business_name",
      text: "Business Name",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "email",
      text: "Email",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "abn",
      text: "ABN",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "phone",
      text: "Phone No.",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "mobile",
      text: "Mobile No.",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "source",
      text: "Source",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    navigate(
      `?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchBeneficary();
  }, [fetchBeneficary]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchBeneficary(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchBeneficary(limit, page);
  };

  const arrayRemove = (arr, value) => {
    return arr.filter(function (ele) {
      return ele !== value;
    });
  };

  const mainSelectRow = {
    mode: "checkbox",
    clickToSelect: false,
    classes: TDClasses,
    selected: selectedRowIds,
    // nonSelectable: xeroData?.connection_status === 1 ? [] : notSelectedRowIds,
    onSelectAll: (row, isSelect) => {
      if (beneficarysList?.length > 0) {
        if (row) {
          const ids =
            isSelect &&
            isSelect?.length > 0 &&
            isSelect?.map((item) => {
              return item?.key;
            });
          setSelectedRowIds(selectedRowIds ? selectedRowIds.concat(ids) : ids);
        } else {
          const removableData =
            isSelect &&
            isSelect?.length > 0 &&
            isSelect.map(function (data) {
              return data?.key;
            });
          setSelectedRowIds(
            selectedRowIds
              ? selectedRowIds.filter((item) => !removableData.includes(item))
              : []
          );
        }
      }
    },
    onSelect: (row, isSelect) => {
      if (isSelect) {
        setSelectedRowIds((keys) => [...keys, row?.key]);
      } else {
        setSelectedRowIds(arrayRemove(selectedRowIds, row?.key));
      }
    },
  };

  // Single Beneficary Delete
  const handleBeneficaryDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.BENEFICARY_DELETE}/${beneficaryId}`);
      setLoading(false);
      fetchBeneficary(limit, page);
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap">
            {/* Sync Xero */}
            {/* <SyncXero
              builderId={builderId}
              selectedRow={selectedRowIds}
              setSelectedRow={setSelectedRowIds}
              trustId={trustId}
              fetchData={() => fetchBeneficary(limit, page)}
              recordId={beneficaryId}
              setRecordId={setBeneficaryId}
              module="beneficary_ids"
              api={API.CONTACT_SYNC}
            /> */}

            {/* Import Beneficary */}
            <UploadBeneficary
              fetchData={() => fetchBeneficary(limit, page)}
              trustId={trustId}
              ButtonName={"Import"}
              setLoading={setLoading}
            />

            {/* Add Beneficary */}
            <ResponsiveDrawerForm
              description={""}
              title={edit ? "Edit Beneficary" : "Add Beneficary"}
              handleOpen={() => {}}
              handleClose={() => {
                setEdit(false);
                setBeneficaryId(null);
                setShowAddEditBeneficary(false);
              }}
              write={true}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add Beneficary</span>
                </div>
              }
              className={"overflow-y-hidden"}
              open={showAddEditBeneficary}
              buttonClassName={""}
              setOpen={setShowAddEditBeneficary}
            >
              <AddEditBeneficary
                edit={edit}
                toggleShowAddEditBeneficary={toggleShowAddEditBeneficary}
                id={beneficaryId}
                trustId={trustId}
                fetchBeneficaryData={() => fetchBeneficary(limit, page)}
              />
            </ResponsiveDrawerForm>
          </div>
        }
        mainClassName="w-[calc(100%-3rem)]"
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class">
            <BootstrapTable
              keyField="key"
              remote
              data={beneficarys}
              selectRow={mainSelectRow}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete Beneficary"}
        open={showBeneficaryDelete}
        setOpen={setShowBeneficaryDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete beneficiary?"}
        okAction={async () => {
          handleBeneficaryDelete();
          toggleShowDelete();
          // fetchBeneficary();
        }}
        cancelAction={() => {
          setBeneficaryId(null);
          setShowBeneficaryDelete(!showBeneficaryDelete);
        }}
      />
    </>
  );
};

export default Beneficiarys;
